import { apiClient } from './api'

export interface CalendlyUrlParams {
  email: string
  name?: string
  phone?: string
  address?: string
  userId?: string
  appointmentType?: string
  notes?: string
  returnUrl?: string
  // Campos adicionales para mejor integración
  companyName?: string
  position?: string
  // UTM tracking
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
}

export interface CalendlyUrlResponse {
  url: string
  generatedAt: string
  parameters: Record<string, string>
}

export interface CalendlyApiResponse<T> {
  success: boolean
  data: T
  message?: string
  error?: string
}

export interface CalendlyAppointment {
  id: string
  calendlyEventId: string
  userEmail: string
  userName: string
  eventName: string
  scheduledAt: string
  startTime: string
  endTime: string
  status: 'scheduled' | 'cancelled' | 'completed'
  timezone: string
  meetingLocation: string
  appointmentType: string
  createdAt: string
  updatedAt: string
}

class CalendlyService {
  /**
   * Genera una URL personalizada de Calendly con datos del usuario
   */
  async generateCalendlyUrl(params: CalendlyUrlParams): Promise<any> {
    try {
      console.log(
        'Enviando petición a /calendly/generate-url con params:',
        params
      )
      const response = await apiClient.post<any>(
        '/calendly/generate-url',
        params
      )
      console.log('Respuesta del servidor completa:', response.data)

      // Devolver directamente la respuesta del backend sin wrapper adicional
      return response.data
    } catch (error: any) {
      console.error('Error completo:', error)
      console.error('Error response:', error.response)
      console.error('Error generating Calendly URL:', error)
      throw new Error(
        error.response?.data?.message || 'Error al generar la URL de Calendly'
      )
    }
  }

  /**
   * Obtiene las citas del usuario actual
   */
  async getUserAppointments(): Promise<
    CalendlyApiResponse<{ count: number; data: CalendlyAppointment[] }>
  > {
    try {
      const response = await apiClient.get<
        CalendlyApiResponse<{ count: number; data: CalendlyAppointment[] }>
      >('/calendly/appointments')
      return response.data
    } catch (error: any) {
      console.error('Error fetching appointments:', error)
      throw new Error(
        error.response?.data?.message || 'Error al obtener las citas'
      )
    }
  }

  /**
   * Obtiene las citas de un usuario específico (requiere permisos de admin)
   */
  async getUserAppointmentsByUserId(
    userId: string
  ): Promise<CalendlyAppointment[]> {
    try {
      const response = await apiClient.get<CalendlyAppointment[]>(
        `/calendly/appointments/user/${userId}`
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching user appointments:', error)
      throw new Error(
        error.response?.data?.message ||
          'Error al obtener las citas del usuario'
      )
    }
  }

  /**
   * Obtiene las citas por email
   */
  async getAppointmentsByEmail(
    email: string
  ): Promise<CalendlyApiResponse<CalendlyAppointment[]>> {
    try {
      const response = await apiClient.get<
        CalendlyApiResponse<CalendlyAppointment[]>
      >('/calendly/appointments/email', {
        params: { email },
      })
      return response.data
    } catch (error: any) {
      console.error('Error fetching appointments by email:', error)
      throw new Error(
        error.response?.data?.message || 'Error al obtener las citas por email'
      )
    }
  }

  /**
   * Cancela una cita
   */
  async cancelAppointment(appointmentId: string): Promise<void> {
    try {
      await apiClient.post(`/calendly/appointments/${appointmentId}/cancel`)
    } catch (error: any) {
      console.error('Error cancelling appointment:', error)
      throw new Error(
        error.response?.data?.message || 'Error al cancelar la cita'
      )
    }
  }

  /**
   * Verifica si el usuario tiene una cita agendada
   * Temporalmente deshabilitado para evitar llamadas innecesarias
   */
  async hasScheduledAppointment(): Promise<boolean> {
    // No realizar verificación automática de citas existentes
    // La vista funciona sin esta verificación
    return false
  }

  /**
   * Genera la URL de retorno basada en el origen actual
   */
  generateReturnUrl(path: string = '/onboarding/next-step'): string {
    const baseUrl = window.location.origin
    return `${baseUrl}${path}`
  }
}

export const calendlyService = new CalendlyService()
